import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { store } from '../../../config/firebase';
import { useColorMode } from '../../../hooks/colorModeProvider';

const getDate = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate();
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isCurrentEvent = (event) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = getDate(event.endDate);

  if (!endDate) {
    return true;
  }

  endDate.setHours(23, 59, 59, 999);
  return endDate >= today;
};

const sortByStartDateDesc = (a, b) => {
  const dateA = getDate(a.startDate);
  const dateB = getDate(b.startDate);

  if (!dateA && !dateB) {
    return String(a.name || '').localeCompare(String(b.name || ''));
  }

  if (!dateA) {
    return 1;
  }

  if (!dateB) {
    return -1;
  }

  return dateB.getTime() - dateA.getTime();
};

const OverlayEventsList = () => {
  const navigate = useNavigate();
  const { mode, setMode } = useColorMode();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  useEffect(() => {
    const eventsRef = collection(store, 'events');
    const q = query(eventsRef);

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const eventsData = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        setEvents(eventsData);
        setLoading(false);
        setError('');
      },
      () => {
        setError('Unable to load events.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const currentEvents = useMemo(() => {
    return events.filter(isCurrentEvent).sort(sortByStartDateDesc);
  }, [events]);

  const handleSelectEvent = (eventId) => {
    navigate(`/overlay-index/${eventId}`);
  };

  return (
    <Box p={2}>
      <Stack spacing={2} alignItems="center">
        <Typography variant="h5" fontWeight={700}>
          Overlay Index Events
        </Typography>

        {loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography>Loading events...</Typography>
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && currentEvents.length === 0 && (
          <Alert severity="info">No current events found.</Alert>
        )}

        {!loading && !error && currentEvents.length > 0 && (
          <Stack spacing={1.25} sx={{ width: '100%', maxWidth: 700 }}>
            {currentEvents.map((event) => {
              const startDate = getDate(event.startDate);

              return (
                <Card key={event.id} sx={{ border: '2px solid', borderColor: 'grey.300' }}>
                  <CardActionArea onClick={() => handleSelectEvent(event.id)}>
                    <CardContent
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          {event.name || 'Unnamed Event'}
                        </Typography>
                        {startDate && (
                          <Typography variant="body2" color="text.secondary">
                            {startDate.toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                      <ChevronRightIcon color="action" />
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default OverlayEventsList;
